if (!customElements.get('collection-search-box')) {
  class CollectionSearchBox extends SearchForm {
    constructor() {
      super();
      this.statusElement = this.querySelector('[data-collection-search-status]');
      this.collectionUrl = this.getAttribute('data-collection-url');
      this.noResultsText = this.getAttribute('data-no-results-text') || '';
      this.loadingText = this.getAttribute('data-loading-text') || '';
      this.resultsCountSuffix = this.getAttribute('data-results-count-suffix') || '';
      this.pageWidthClass = this.getAttribute('data-page-width-class') || '';

      this.gridContainer = document.getElementById(this.getAttribute('data-grid-target'));
      this.filtersContainer = document.getElementById(this.getAttribute('data-filters-target'));
      this.resultsContainer = document.getElementById(this.getAttribute('data-results-target'));

      if (this.resultsContainer && this.pageWidthClass) {
        this.resultsContainer.classList.add(this.pageWidthClass);
      }

      this.products = [];
      this.indexLoaded = false;
      this.indexRequested = false;

      if (this.input && this.input.form) {
        this.input.form.addEventListener('submit', (event) => event.preventDefault());
        // The reset ("x") button clears the input via the form's native
        // reset behavior (handled by the base SearchForm class), which does
        // not fire an "input" event — so onChange() never runs on its own
        // and the search results stay on screen instead of reverting to the
        // collection's normal product grid. Registering after super()'s own
        // "reset" listener means this runs once the input is already empty.
        this.input.form.addEventListener('reset', () => this.onChange());
      }

      // Defer fetching the full product index until the shopper actually
      // shows intent to search, instead of on every page load — this
      // matters a lot on large collections (hundreds/thousands of products).
      if (this.input) {
        this.input.addEventListener('focus', () => this.ensureIndexLoaded(), { once: true });
      }
    }

    ensureIndexLoaded() {
      if (this.indexRequested) return;
      this.indexRequested = true;
      this.loadIndex();
    }

    async loadIndex() {
      if (!this.collectionUrl) return;

      const firstPage = await this.fetchIndexPage(1);
      if (!firstPage) {
        this.indexLoaded = true;
        return;
      }

      this.products = this.products.concat(firstPage.products);
      const totalPages = firstPage.pages || 1;

      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        const remainingPages = await Promise.all(pageNumbers.map((page) => this.fetchIndexPage(page)));
        remainingPages.forEach((page) => {
          if (page) this.products = this.products.concat(page.products);
        });
      }

      this.indexLoaded = true;

      // The user may have typed a query before the full index finished loading.
      if (this.getQuery()) this.renderResults(this.getQuery());
    }

    async fetchIndexPage(page) {
      try {
        const url = `${this.collectionUrl}?section_id=collection-search-index&page=${page}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const html = await response.text();
        const match = html.match(/<script[^>]*data-collection-search-index[^>]*>([\s\S]*?)<\/script>/);
        if (!match) return null;
        return JSON.parse(match[1]);
      } catch (error) {
        return null;
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const term = this.getQuery();

      if (!term.length) {
        this.showOriginalGrid();
        return;
      }

      this.ensureIndexLoaded();
      this.renderResults(term);
    }

    renderResults(term) {
      if (!this.resultsContainer) return;

      const needle = term.toLowerCase();
      const matches = this.products.filter((product) => product.title.toLowerCase().includes(needle));

      this.showSearchResults();

      if (matches.length === 0 && !this.indexLoaded) {
        this.setMessage(this.loadingText);
        return;
      }

      if (matches.length === 0) {
        this.setMessage(this.noResultsText);
        return;
      }

      const fragment = document.createDocumentFragment();
      matches.forEach((product) => fragment.appendChild(this.renderCard(product)));

      this.resultsContainer.innerHTML = '';
      this.resultsContainer.appendChild(fragment);

      if (this.statusElement) {
        this.statusElement.textContent = `${matches.length}${this.resultsCountSuffix}`;
      }
    }

    renderCard(product) {
      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'c-item__unit';

      const image = document.createElement('img');
      image.src = product.image || '';
      image.width = 300;
      image.height = 300;
      image.loading = 'lazy';
      image.alt = '';
      link.appendChild(image);

      const name = document.createElement('p');
      name.className = 'c-item__unit-name';
      name.textContent = product.title;
      link.appendChild(name);

      const info = document.createElement('div');
      info.className = 'c-item__unit-info';

      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = product.price;
      info.appendChild(price);

      if (!product.available) {
        const sold = document.createElement('span');
        sold.className = 'sold';
        sold.textContent = 'SOLD OUT';
        info.appendChild(sold);
      }

      link.appendChild(info);
      return link;
    }

    setMessage(text) {
      const message = document.createElement('p');
      message.className = 'collection-search__message';
      message.textContent = text;
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.appendChild(message);
      // The message is already visible inside the results area — the status
      // line is only for the "N results found" count, so leave it blank here
      // instead of repeating the same text a second time on screen.
      if (this.statusElement) this.statusElement.textContent = '';
    }

    showSearchResults() {
      if (this.gridContainer) this.gridContainer.hidden = true;
      if (this.filtersContainer) this.filtersContainer.hidden = true;
      if (this.resultsContainer) this.resultsContainer.hidden = false;
    }

    showOriginalGrid() {
      if (this.gridContainer) this.gridContainer.hidden = false;
      if (this.filtersContainer) this.filtersContainer.hidden = false;
      if (this.resultsContainer) {
        this.resultsContainer.hidden = true;
        this.resultsContainer.innerHTML = '';
      }
      if (this.statusElement) this.statusElement.textContent = '';
    }
  }

  customElements.define('collection-search-box', CollectionSearchBox);
}
